import { createContext, useContext, Dispatch } from 'react';

export type SaveResult = 'committed' | 'cancelled' | 'noop';

export type ActiveEditController = {
    editKey: number;
    isDirty: () => boolean;
    save: () => Promise<SaveResult> | SaveResult;
    discard: () => void | Promise<void>;
} | null;

export type GuardIntent =
    | { type: 'ACTIVATE_EDIT'; fromEditKey: number; toEditKey: number | null }
    | { type: 'NAVIGATE_LINK'; to: string }
    | { type: 'NAVIGATE_BROWSER' }
    | { type: 'UI_ACTION'; target: 'SERVICE_MENU' | 'NAV_MENU' };

export type RootEditState = {
    activeEditKey: number | null;
    activeIsDirty: boolean;
    guard: {
        isOpen: boolean;
        intent: GuardIntent | null;
        isWorking: boolean;
    };
};

// Export the reducer action type so edit-only components can dispatch.
export type RootEditAction =
    | { type: 'setActiveEditKey'; editKey: number | null }
    | { type: 'setActiveIsDirty'; isDirty: boolean }
    | { type: 'openGuard'; intent: GuardIntent }
    | { type: 'closeGuard' }
    | { type: 'setGuardWorking'; isWorking: boolean };

export type RootEditActions = {
    requestActivateEdit: (toEditKey: number | null) => void;
    setActiveController: (controller: ActiveEditController) => void;
    getActiveController: () => ActiveEditController;
    guardNavigateLink: (
        to: string,
        proceed: () => void | Promise<void>,
    ) => void;
    guardUiAction: (
        target: 'SERVICE_MENU' | 'NAV_MENU',
        proceed: () => void | Promise<void>,
    ) => void;
    setPendingProceed: (fn: null | (() => void | Promise<void>)) => void;
    getPendingProceed: () => null | (() => void | Promise<void>);
    clearPendingProceed: () => void;
};

export const RootEditStateContext = createContext<RootEditState | null>(null);
export const RootEditDispatchContext =
    createContext<Dispatch<RootEditAction> | null>(null);
export const RootEditActionsContext = createContext<RootEditActions | null>(
    null,
);

export function useRootEditState(): RootEditState {
    const ctx = useContext(RootEditStateContext);
    if (!ctx)
        throw new Error(
            'useRootEditState must be used within RootEditProvider',
        );
    return ctx;
}

export function useRootEditStateDispatch(): Dispatch<RootEditAction> {
    const ctx = useContext(RootEditDispatchContext);
    if (!ctx)
        throw new Error(
            'useRootEditStateDispatch must be used within RootEditProvider',
        );
    return ctx;
}

export function useRootEditActions(): RootEditActions {
    const ctx = useContext(RootEditActionsContext);
    if (!ctx)
        throw new Error(
            'useRootEditActions must be used within RootEditProvider',
        );
    return ctx;
}
